import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { fetchProfile } from '../../services/profileService';
import { claimPendingSubscription } from '../../services/stripeService';
import { isSubscriptionActive } from '../../lib/subscription';
import LoadingSpinner from '../LoadingSpinner';

interface SubscriptionRouteProps {
  children: React.ReactNode;
}

/**
 * Requires logged-in user with active or trialing Stripe subscription.
 * Settings and /subscribe remain on ProtectedRoute only.
 */
const SubscriptionRoute: React.FC<SubscriptionRouteProps> = ({ children }) => {
  const { user, loading: authLoading, session } = useAuth();
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [subscribed, setSubscribed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!user?.id || !session) {
        setChecking(false);
        setSubscribed(false);
        return;
      }

      try {
        setChecking(true);
        if (session.access_token) {
          try {
            await claimPendingSubscription(session.access_token);
          } catch (claimErr) {
            console.warn('SubscriptionRoute: pending claim failed', claimErr);
          }
        }
        const profile = await fetchProfile(user.id);
        const ok = isSubscriptionActive(profile?.subscription_status);
        if (!cancelled) {
          setSubscribed(ok);
        }
      } catch (e) {
        console.error('SubscriptionRoute:', e);
        if (!cancelled) {
          setSubscribed(false);
        }
      } finally {
        if (!cancelled) {
          setChecking(false);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
    // Intentionally excluding `session` from deps: supabase-js refreshes the
    // session (new object identity) on tab focus, which would otherwise flip
    // `checking` back to true and flash a full-screen spinner that looks like
    // a page reload. The subscription check only needs user.id + pathname.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, location.pathname]);

  if (authLoading || checking) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <LoadingSpinner />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (!subscribed) {
    return <Navigate to="/subscribe" replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
};

export default SubscriptionRoute;
